/**
 * PlayerSubmenuItem View Component
 *
 * Individual player item displayed in a submenu list.
 * @module components/HeaderCreateMenu/PlayerSubmenuItem
 */

import { User } from "lucide-react-native";
import { memo } from "react";
import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/**
 * Recent player item for display in menus
 */
interface RecentPlayerItem {
  id: string;
  name: string;
}

/**
 * Props for PlayerSubmenuItemView component
 */
interface PlayerSubmenuItemViewProps {
  player: RecentPlayerItem;
  onPress: () => void;
  itemClassName: string;
  iconBoxClassName: string;
  iconClassName: string;
  nameClassName: string;
}

/**
 * Displays a single player item with icon and name in a submenu.
 * @param root0 - Component props
 * @param root0.player - Player item data to display
 * @param root0.onPress - Callback when item is pressed
 * @param root0.itemClassName - Class name for the item container
 * @param root0.iconBoxClassName - Class name for icon container
 * @param root0.iconClassName - Class name for the icon
 * @param root0.nameClassName - Class name for player name text
 */
const PlayerSubmenuItemView = ({
  player,
  onPress,
  itemClassName,
  iconBoxClassName,
  iconClassName,
  nameClassName,
}: PlayerSubmenuItemViewProps) => (
  <Pressable
    onPress={onPress}
    accessible
    accessibilityLabel={`Select ${player.name}`}
    accessibilityRole="button"
    className={itemClassName}
  >
    <Box className={iconBoxClassName}>
      <Icon as={User} size="xs" className={iconClassName} />
    </Box>
    <Text variant="unstyled" className={nameClassName} isTruncated>
      {player.name}
    </Text>
  </Pressable>
);

PlayerSubmenuItemView.displayName = "PlayerSubmenuItemView";

export default memo(PlayerSubmenuItemView);
